import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { hasLink } from '@ngxp/rest';
import {
	VorgangFacade,
	VorgangLinkRel,
	VorgangResource,
	VorgangStatus,
	VorgangStatusLabel,
	VorgangTypLabel,
	VorgangTypLabelLFPR,
	VorgangTypLabelSchiff,
} from '@schir-int-client/vorgang-shared';
import {
	DialogService,
	EnumToLabelPipe,
	HandlesBackdropClickAndEscapeKey,
	JaNeinLabels,
	NotizDialogComponent,
} from '@schir-int-client/tech';
import { Observable, Subscription } from 'rxjs';
import { ChangeVorgangStatusDialogComponent } from '@schir-int-client/vorgang';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiRootFacade } from '@schir-int-client/api-root';
import { isNil } from 'lodash-es';
import { take } from 'rxjs/operators';
import { getId } from '../../../../../test-helpers/src/lib/resource-helper';

@Component({
	selector: 'schir-int-client-vorgang-in-verfahren',
	templateUrl: './vorgang-in-verfahren.component.html',
	styleUrls: ['./vorgang-in-verfahren.component.scss'],
	standalone: false,
})
export class VorgangInVerfahrenComponent
	extends HandlesBackdropClickAndEscapeKey<NotizDialogComponent | ChangeVorgangStatusDialogComponent>
	implements OnInit, OnDestroy
{
	@Input() vorgang: VorgangResource;
	@Input() selected: boolean;
	@Output() selectedChange: EventEmitter<VorgangResource> = new EventEmitter<VorgangResource>();
	@Output() notizChange: EventEmitter<string> = new EventEmitter<string>();

	vorgangsTypen: VorgangTypLabel;
	vorgangStatusLabel = VorgangStatusLabel;
	private subscriptions: Subscription[] = [];
	deletable: Observable<boolean>;

	protected readonly VorgangStatus = VorgangStatus;

	constructor(
		private vorgangFacade: VorgangFacade,
		protected dialogService: DialogService,
		private enumToLabel: EnumToLabelPipe,
		private router: Router,
		private activeRoute: ActivatedRoute,
		private apiRootFacade: ApiRootFacade
	) {
		super(dialogService);
	}

	ngOnInit(): void {
		if (this.selected) {
			this.setSelectedVorgang();
			// do not navigate if already on that vorgang:
			if (!this.router.url.includes(this.getVorgangId())) {
				this.router.navigate(
					[
						{
							outlets: {
								vorgang: this.getVorgangId(),
								verfuegung: null,
								aufgabe: null,
							},
						},
					],
					{ relativeTo: this.activeRoute }
				);
			}
		}

		this.subscriptions.push(
			this.apiRootFacade
				.getCurrentApiRoot()
				.subscribe(
					apiRoot =>
						(this.vorgangsTypen = apiRoot.features.lfpr ? VorgangTypLabelLFPR : VorgangTypLabelSchiff)
				)
		);

		this.deletable = this.vorgangFacade.isDeletable(this.vorgang);
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	setSelectedVorgang() {
		this.selectedChange.emit(this.vorgang);
	}

	openNotizDialog() {
		const title = 'Notizen zu Vorgang ' + this.enumToLabel.transform(this.vorgang.typ, this.vorgangsTypen);

		this.matDialogRef = this.dialogService.openEditorDialog(this, NotizDialogComponent, {
			data: {
				title: title,
				notiz: this.vorgang.notiz,
			},
		});

		const editor: NotizDialogComponent = <NotizDialogComponent>this.matDialogRef.componentInstance;

		this.subscriptions.push(
			editor.notizEvent.pipe(take(1)).subscribe(data => {
				this.updateNotiz(data);
			})
		);
	}

	updateNotiz(notiz: string) {
		this.notizChange.emit(notiz);
	}

	getVorgangId() {
		return getId(this.vorgang);
	}

	openStatusDialog() {
		this.setSelectedVorgang();
		this.dialogService.openEditorDialog(this, ChangeVorgangStatusDialogComponent, {
			data: {
				vorgang: this.vorgang,
			},
		});
	}

	editNotizAllowed(): boolean {
		return hasLink(this.vorgang, VorgangLinkRel.UPDATE_NOTIZ);
	}

	statusChangeAllowed(): boolean {
		return (
			hasLink(this.vorgang, VorgangLinkRel.MARK_AS_WIEDERVORLAGE) &&
			hasLink(this.vorgang, VorgangLinkRel.MARK_AS_RECHTSPFLEGE) &&
			hasLink(this.vorgang, VorgangLinkRel.MARK_AS_ABGESCHLOSSEN) &&
			hasLink(this.vorgang, VorgangLinkRel.MARK_AS_RUECKLAUF) &&
			hasLink(this.vorgang, VorgangLinkRel.PROPOSE_WIEDERVORLAGEDATUM)
		);
	}

	openDeleteDialog() {
		let typ = this.vorgang.typ;
		if (VorgangTypLabelSchiff[typ]) {
			typ = VorgangTypLabelSchiff[typ];
		} else if (VorgangTypLabelLFPR[typ]) {
			typ = VorgangTypLabelLFPR[typ];
		}

		const message = 'Möchten Sie den ausgewählten Vorgang ' + typ + ' wirklich löschen?';
		const additionalContents = isNil(this.vorgang.notiz) ? [] : ['Notiz: ' + this.vorgang.notiz];

		this.dialogService
			.openDialogYesNo(message, JaNeinLabels, [], additionalContents)
			.pipe(take(1))
			.subscribe(data => {
				if (data.agree) {
					this.vorgangFacade.deleteVorgang(this.vorgang);
				}
			});
	}
}
